/** Party-revealed, top-down Dungeon Map. Unseen rooms remain void. */
import type { Observation } from "@pd/shared";
import { keyLetter, playerColor } from "./actions.js";

interface Point { x: number; y: number }
interface Label { x: number; y: number; w: number; h: number; name: string; color: string }
interface HitArea { x: number; y: number; w: number; h: number }

const FLOOR = "#334145";
const WALL = "#263237";
const EDGE = "#657276";
const GOLD = "#d9ae55";
const GUARD = "#c23b2e";

export class MapOverlay {
  private el: HTMLDivElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private closeButton: HTMLButtonElement;
  private abort = new AbortController();
  private obs: Observation | null = null;
  private meId = 0;
  private visible = false;
  private previousFocus: HTMLElement | null = null;
  private hitAreas: HitArea[] = [];

  constructor(root: HTMLElement, private nameFor: (id: number) => string) {
    this.el = document.createElement("div");
    this.el.id = "map-overlay";
    this.el.setAttribute("role", "dialog");
    this.el.setAttribute("aria-modal", "true");
    this.el.setAttribute("aria-label", "Dungeon Map");
    this.canvas = document.createElement("canvas");
    this.ctx = this.canvas.getContext("2d")!;
    this.closeButton = document.createElement("button");
    this.closeButton.type = "button";
    this.closeButton.className = "map-close";
    this.closeButton.setAttribute("aria-label", "Close map");
    this.closeButton.textContent = "×";
    this.el.append(this.canvas, this.closeButton);
    root.appendChild(this.el);

    this.closeButton.addEventListener("click", () => this.close(), { signal: this.abort.signal });
    this.canvas.addEventListener("click", (ev) => {
      if (ev.button !== 0 || !this.visible) return;
      const rect = this.canvas.getBoundingClientRect();
      const x = (ev.clientX - rect.left) * this.el.clientWidth / rect.width;
      const y = (ev.clientY - rect.top) * this.el.clientHeight / rect.height;
      if (!this.hitAreas.some((area) => x >= area.x && x <= area.x + area.w && y >= area.y && y <= area.y + area.h)) {
        this.close();
      }
    }, { signal: this.abort.signal });
    this.el.addEventListener("keydown", (ev) => {
      if (ev.key === "Escape" || ev.key.toLowerCase() === "m") {
        ev.preventDefault();
        this.close();
      } else if (ev.key === "Tab") {
        ev.preventDefault();
        this.closeButton.focus();
      }
      // Keep gameplay hotkeys from firing behind the map.
      ev.stopPropagation();
    }, { signal: this.abort.signal });
    window.addEventListener("resize", () => this.draw(), { signal: this.abort.signal });
  }

  show(obs: Observation, meId: number): void {
    if (this.visible) return;
    this.obs = obs;
    this.meId = meId;
    this.visible = true;
    this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.el.classList.add("show");
    this.closeButton.focus();
    this.draw();
  }

  toggle(obs: Observation, meId: number): void {
    if (this.visible) this.close();
    else this.show(obs, meId);
  }

  update(obs: Observation, meId: number): void {
    this.obs = obs;
    this.meId = meId;
    if (this.visible) this.draw();
  }

  destroy(): void {
    this.abort.abort();
    this.el.remove();
  }

  private close(): void {
    if (!this.visible) return;
    this.visible = false;
    this.el.classList.remove("show");
    if (this.previousFocus?.isConnected) this.previousFocus.focus();
    this.previousFocus = null;
  }

  private draw(): void {
    const obs = this.obs;
    if (!obs || !this.visible) return;
    const w = this.el.clientWidth;
    const h = this.el.clientHeight;
    this.hitAreas = [];
    if (w <= 0 || h <= 0) return;
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    const ctx = this.ctx;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const background = ctx.createRadialGradient(w / 2, h * 0.48, 0, w / 2, h * 0.48, Math.max(w, h) * 0.65);
    background.addColorStop(0, "#1e292d");
    background.addColorStop(0.7, "#10191c");
    background.addColorStop(1, "#0c1417");
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, w, h);

    const tiles = obs.combat.roomTiles;
    const stride = tiles + 1;
    const cols = Math.round((obs.width + 1) / stride);
    const rows = Math.round((obs.height + 1) / stride);
    if (cols < 1 || rows < 1 || tiles < 1) return;
    const pitch = Math.min(150, (w - 64) / cols, (h - 72) / rows);
    const gap = Math.max(4, Math.min(7, pitch * 0.08));
    const box = pitch - gap;
    const ox = (w - cols * pitch) / 2;
    const oy = (h - rows * pitch) / 2;
    const roomLeft = (rx: number): number => ox + rx * pitch;
    const roomTop = (ry: number): number => oy + ry * pitch;
    const roomOf = (x: number, y: number): Point => ({ x: Math.floor(x / stride), y: Math.floor(y / stride) });
    const tilePoint = (x: number, y: number): Point => {
      const r = roomOf(x, y);
      return {
        x: roomLeft(r.x) + ((x % stride) + 0.5) * box / tiles,
        y: roomTop(r.y) + ((y % stride) + 0.5) * box / tiles,
      };
    };

    // A room is revealed if its center tile is in the party mask. The grid
    // dimensions reveal no room topology: void and unseen cells stay blank.
    for (let ry = 0; ry < rows; ry++) {
      for (let rx = 0; rx < cols; rx++) {
        const sx = rx * stride + Math.floor(tiles / 2);
        const sy = ry * stride + Math.floor(tiles / 2);
        if (obs.revealed[sy * obs.width + sx] !== 1) continue;
        const x = roomLeft(rx);
        const y = roomTop(ry);
        this.hitAreas.push({ x, y, w: box, h: box });
        ctx.fillStyle = FLOOR;
        ctx.fillRect(x, y, box, box);
        ctx.strokeStyle = EDGE;
        ctx.lineWidth = 2;
        ctx.strokeRect(x + 1, y + 1, box - 2, box - 2);
        ctx.strokeStyle = "rgba(123, 139, 135, 0.13)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (let t = 1; t < tiles; t++) {
          const at = t * box / tiles;
          ctx.moveTo(x + at, y + 3);
          ctx.lineTo(x + at, y + box - 3);
          ctx.moveTo(x + 3, y + at);
          ctx.lineTo(x + box - 3, y + at);
        }
        ctx.stroke();
        // Furnished obstacles remain readable without competing with markers.
        for (let ty = 0; ty < tiles; ty++) {
          for (let tx = 0; tx < tiles; tx++) {
            const i = (ry * stride + ty) * obs.width + rx * stride + tx;
            if (obs.map[i] === 1) continue;
            ctx.fillStyle = obs.terrain[i] === 2 ? "#304d55" : obs.terrain[i] === 3 ? "#50504b" : WALL;
            const cell = box / tiles;
            ctx.fillRect(x + tx * cell + 0.5, y + ty * cell + 0.5, Math.max(1, cell - 1), Math.max(1, cell - 1));
          }
        }
      }
    }

    // Every door is on a shared wall, not in a corridor.
    for (const door of obs.doors) {
      const r = roomOf(door.x, door.y);
      const cx = door.axis === "h" ? roomLeft(r.x + 1) - gap / 2 : roomLeft(r.x) + box / 2;
      const cy = door.axis === "v" ? roomTop(r.y + 1) - gap / 2 : roomTop(r.y) + box / 2;
      this.hitAreas.push({ x: cx - 13, y: cy - 13, w: 26, h: 26 });
      if (door.open) {
        // Once a key or guardian door opens, its lock mark has served its purpose.
        // Keep only the quiet passage mark even though its original kind is observed.
        ctx.fillStyle = "#697b7c";
        ctx.strokeStyle = "#aab9ae";
        ctx.lineWidth = 1;
        const dw = door.axis === "h" ? 5 : 10;
        const dh = door.axis === "v" ? 5 : 10;
        ctx.fillRect(cx - dw / 2, cy - dh / 2, dw, dh);
        ctx.strokeRect(cx - dw / 2, cy - dh / 2, dw, dh);
        continue;
      }
      const badge = Math.max(17, Math.min(26, pitch * 0.28));
      if (door.puzzleLocked) {
        const s = badge / 2;
        ctx.fillStyle = "#654d86"; ctx.strokeStyle = "#d1b6ef"; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(cx, cy - s); ctx.lineTo(cx + s, cy); ctx.lineTo(cx, cy + s); ctx.lineTo(cx - s, cy); ctx.closePath(); ctx.fill(); ctx.stroke();
      } else if (door.guarded) {
        ctx.shadowColor = GUARD;
        ctx.shadowBlur = 8;
        ctx.fillStyle = "#682f30";
        ctx.strokeStyle = "#e46a5f";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(cx, cy, badge / 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.shadowBlur = 0;
        // The world door's red diamond and crossbar ward.
        const s = badge * 0.3;
        ctx.strokeStyle = "#ffb3a6";
        ctx.lineWidth = 1.8;
        ctx.beginPath();
        ctx.moveTo(cx, cy - s);
        ctx.lineTo(cx + s, cy);
        ctx.lineTo(cx, cy + s);
        ctx.lineTo(cx - s, cy);
        ctx.closePath();
        ctx.moveTo(cx - s * 0.75, cy);
        ctx.lineTo(cx + s * 0.75, cy);
        ctx.stroke();
      } else if (door.keyId >= 0) {
        ctx.shadowColor = GOLD;
        ctx.shadowBlur = 8;
        ctx.fillStyle = GOLD;
        ctx.strokeStyle = "#fff1bd";
        ctx.lineWidth = 1.5;
        ctx.fillRect(cx - badge / 2, cy - badge / 2, badge, badge);
        ctx.strokeRect(cx - badge / 2, cy - badge / 2, badge, badge);
        ctx.shadowBlur = 0;
        ctx.fillStyle = "#192224";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.font = `900 ${Math.round(badge * 0.61)}px "Avenir Next", "Segoe UI", sans-serif`;
        ctx.fillText(keyLetter(door.keyId), cx, cy + 1);
      }
    }

    // Floor keys and gatestones fan out if several share a tile.
    const stacks = new Map<string, { kind: "key" | "stone"; x: number; y: number; id: number; color: string }[]>();
    for (const item of obs.keys) {
      const at = `${item.x},${item.y}`;
      const stack = stacks.get(at) ?? [];
      stack.push({ kind: "key", x: item.x, y: item.y, id: item.id, color: GOLD });
      stacks.set(at, stack);
    }
    for (const item of obs.stones) {
      if (item.state !== "floor" || item.x === undefined || item.y === undefined) continue;
      const at = `${item.x},${item.y}`;
      const stack = stacks.get(at) ?? [];
      stack.push({ kind: "stone", x: item.x, y: item.y, id: item.id, color: item.kind === "group" ? GOLD : "#4fb3a7" });
      stacks.set(at, stack);
    }
    for (const stack of stacks.values()) {
      stack.forEach((item, i) => {
        const p = tilePoint(item.x, item.y);
        p.x += (i - (stack.length - 1) / 2) * 11;
        if (item.kind === "key") {
          ctx.strokeStyle = item.color;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(p.x - 5, p.y - 4, 4, 0, Math.PI * 2);
          ctx.moveTo(p.x - 2, p.y - 1);
          ctx.lineTo(p.x + 5, p.y + 6);
          ctx.moveTo(p.x + 3, p.y + 4);
          ctx.lineTo(p.x + 3, p.y + 8);
          ctx.stroke();
          ctx.fillStyle = "#f1d390";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.font = '800 11px "Avenir Next", sans-serif';
          ctx.fillText(keyLetter(item.id), p.x + 8, p.y + 1);
        } else {
          ctx.fillStyle = item.color;
          ctx.strokeStyle = "#dbe8dc";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 5, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        }
      });
    }

    const base = tilePoint(obs.base.x, obs.base.y);
    ctx.fillStyle = "#79bc8b";
    ctx.strokeStyle = "#dcf4d9";
    ctx.lineWidth = 1.5;
    ctx.fillRect(base.x - 8, base.y - 8, 16, 16);
    ctx.strokeRect(base.x - 8, base.y - 8, 16, 16);
    if (obs.returnPortal) {
      const p = tilePoint(obs.returnPortal.x, obs.returnPortal.y);
      ctx.strokeStyle = "#6ea8c4";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 8, 0, Math.PI * 2);
      ctx.stroke();
    }
    if (obs.boss) {
      const p = tilePoint(obs.boss.x, obs.boss.y);
      ctx.fillStyle = obs.boss.state === "portal" ? GOLD : "#dd796e";
      ctx.strokeStyle = obs.boss.state === "portal" ? "#fff1bd" : "#ffd1bc";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y - 9);
      ctx.lineTo(p.x + 9, p.y);
      ctx.lineTo(p.x, p.y + 9);
      ctx.lineTo(p.x - 9, p.y);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }

    const labels: Label[] = [];
    for (const player of obs.players) {
      const p = tilePoint(player.x, player.y);
      const color = playerColor(player.id);
      ctx.fillStyle = color;
      ctx.strokeStyle = "#e6f4ed";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      if (player.id === this.meId) {
        ctx.strokeStyle = "#e8dfb4";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 10.5, 0, Math.PI * 2);
        ctx.stroke();
      }
      const name = this.nameFor(player.id);
      ctx.font = '750 12px "Avenir Next", "Segoe UI", sans-serif';
      const width = ctx.measureText(name).width + 13;
      const height = 19;
      let y = p.y - 7 - 8 - height;
      while (labels.some((other) => p.x - width / 2 < other.x + other.w && p.x + width / 2 > other.x && y < other.y + other.h && y + height > other.y)) {
        y -= height + 2;
      }
      labels.push({ x: p.x - width / 2, y, w: width, h: height, name, color });
    }
    for (const label of labels) {
      this.hitAreas.push({ x: label.x, y: label.y, w: label.w, h: label.h });
      ctx.fillStyle = "#142225";
      ctx.strokeStyle = label.color;
      ctx.lineWidth = 1;
      ctx.fillRect(label.x, label.y, label.w, label.h);
      ctx.strokeRect(label.x, label.y, label.w, label.h);
      ctx.fillStyle = "#fff8e8";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = '750 12px "Avenir Next", "Segoe UI", sans-serif';
      ctx.fillText(label.name, label.x + label.w / 2, label.y + label.h / 2 + 1);
    }
  }
}
